import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, Sun, Moon, Globe, ChevronDown, Check } from 'lucide-react';
import logomark from 'assets/images/logos/Logomark.svg';

const languages = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Français' },
];

const Navbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  // Theme state — read from localStorage directly so landing page works outside ThemeProvider
  // Default to system preference when no saved preference exists
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('famous-clicker-theme');
    if (saved === 'light') return false;
    if (saved === 'dark') return true;
    // 'system' or no saved preference — follow OS
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const toggleTheme = () => {
    const next = !isDark;
    setIsDark(next);
    localStorage.setItem('famous-clicker-theme', next ? 'dark' : 'light');
    if (next) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  // Sync dark class on mount (landing page is outside ThemeProvider)
  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code);
    setIsLangOpen(false);
  };

  const navLinks = [
    { label: t('landing.nav.features'), href: '#features' },
    { label: t('landing.nav.testimonials'), href: '#testimonials' },
    { label: t('landing.nav.pricing'), href: '#pricing' },
  ];

  return (
    <nav
      className={`fixed top-0 w-full z-50 backdrop-blur-sm transition-shadow duration-200 ${
        isScrolled ? 'shadow-sm' : ''
      }`}
      style={{
        background: isDark ? 'rgba(29, 41, 61, 0.95)' : 'rgba(255, 255, 255, 0.95)',
      }}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 flex items-center justify-between h-[72px]">
        {/* Logo */}
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a href="#" className="shrink-0 flex items-center gap-2">
          <img src={logomark} alt="" className="h-8 w-8" />
          <span
            className="font-geist text-lg"
            style={{ color: isDark ? '#FFFFFF' : undefined }}
          >
            Famous<span className="font-bold">Clicker</span>
          </span>
        </a>

        {/* Desktop Nav Links */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="font-geist text-sm font-medium transition-colors"
              style={{ color: isDark ? '#90A1B9' : '#62748E' }}
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* Desktop Actions */}
        <div className="hidden md:flex items-center gap-3">
          {/* Language Selector */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setIsLangOpen(!isLangOpen)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-colors"
              style={{
                borderColor: isDark ? '#314158' : '#E2E8F0',
                color: isDark ? '#90A1B9' : '#62748E',
              }}
              aria-label="Select language"
            >
              <Globe size={16} />
              <span className="font-geist text-sm font-medium">{currentLang.name}</span>
              <ChevronDown
                size={14}
                className={`transition-transform ${isLangOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {isLangOpen && (
              <div
                className="absolute top-full right-0 mt-1 rounded-xl shadow-lg overflow-hidden min-w-[160px] z-50"
                style={{
                  background: isDark ? '#0F172B' : '#FFFFFF',
                  border: `1px solid ${isDark ? '#314158' : '#E2E8F0'}`,
                }}
              >
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className="flex items-center justify-between w-full px-4 py-2.5 text-left font-geist text-sm transition-colors"
                    style={{
                      color: currentLang.code === lang.code
                        ? '#2B7FFF'
                        : isDark ? '#E2E8F0' : '#314158',
                      background: currentLang.code === lang.code
                        ? isDark ? '#162456' : '#EFF6FF'
                        : 'transparent',
                      fontWeight: currentLang.code === lang.code ? 500 : 400,
                    }}
                  >
                    {lang.name}
                    {currentLang.code === lang.code && <Check size={14} />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg border transition-colors"
            style={{
              borderColor: isDark ? '#314158' : '#E2E8F0',
              color: isDark ? '#90A1B9' : '#62748E',
            }}
            aria-label="Toggle theme"
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
          </button>

          {/* Login */}
          <Link
            to="/login"
            className="font-geist text-sm font-medium transition-colors"
            style={{ color: isDark ? '#90A1B9' : '#62748E' }}
          >
            {t('landing.nav.login')}
          </Link>

          {/* CTA */}
          <Link
            to="/registration"
            className="gradient-btn inline-flex items-center justify-center bg-fc-gradient-btn text-white font-geist font-medium rounded-xl px-5 py-2.5 text-sm"
          >
            {t('landing.nav.startTrial')}
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2"
          style={{ color: isDark ? '#E2E8F0' : '#314158' }}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden px-6 py-4 space-y-4"
          style={{
            background: isDark ? '#1D293D' : '#FFFFFF',
            borderTop: `1px solid ${isDark ? '#314158' : '#F1F5F9'}`,
          }}
        >
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setIsMobileMenuOpen(false)}
              className="block font-geist text-sm font-medium"
              style={{ color: isDark ? '#90A1B9' : '#62748E' }}
            >
              {link.label}
            </a>
          ))}

          {/* Mobile: Language & Theme */}
          <div className="flex items-center gap-3 pt-2">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className="font-geist text-sm px-3 py-1.5 rounded-lg border transition-colors"
                style={{
                  borderColor: currentLang.code === lang.code
                    ? '#2B7FFF'
                    : isDark ? '#314158' : '#E2E8F0',
                  background: currentLang.code === lang.code
                    ? isDark ? '#162456' : '#EFF6FF'
                    : 'transparent',
                  color: currentLang.code === lang.code
                    ? '#2B7FFF'
                    : isDark ? '#90A1B9' : '#62748E',
                  fontWeight: currentLang.code === lang.code ? 500 : 400,
                }}
              >
                {lang.name}
              </button>
            ))}
            <button
              onClick={toggleTheme}
              className="ml-auto p-2 rounded-lg border"
              style={{
                borderColor: isDark ? '#314158' : '#E2E8F0',
                color: isDark ? '#90A1B9' : '#62748E',
              }}
              aria-label="Toggle theme"
            >
              {isDark ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          </div>

          <div
            className="pt-4 space-y-3"
            style={{ borderTop: `1px solid ${isDark ? '#314158' : '#F1F5F9'}` }}
          >
            <Link
              to="/login"
              className="block font-geist text-sm font-medium"
              style={{ color: isDark ? '#90A1B9' : '#62748E' }}
            >
              {t('landing.nav.login')}
            </Link>
            <Link
              to="/registration"
              className="gradient-btn block text-center bg-fc-gradient-btn text-white font-geist font-medium rounded-xl px-5 py-2.5 text-sm"
            >
              {t('landing.nav.startTrial')}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
